/**
 * The single source for words that name domain concepts.
 *
 * Design and reasoning: `documentation/engineering/terms-and-vocabulary.md`. Decisions: D-026 to
 * D-030, and D-038 which performed the first real rename through this registry. The two rules that
 * shape everything here:
 *
 * 1. KEYS NAME THE INTERNAL DOMAIN, VALUES NAME WHAT THE USER SEES. The key is `loginGroup` because
 *    the entity is `LoginGroup`; the value was "Member" and is now "Company". Keys and values have
 *    different lifetimes, so keying on the current word would have made that rename a KEY rename,
 *    and would have left `terms.member.value` returning "Company" afterwards. The key did not move.
 * 2. ONLY `value` AND `plural` ARE AUTHORED. Every other form is derived, because an authored
 *    lowercase or possessive is a place for drift to reappear inside a single term: change `value`
 *    from Member to Company, forget the sibling, and the exercise has failed at the smallest
 *    possible scale.
 */

// THE `.ts` EXTENSION IS LOAD-BEARING - do not tidy it away. `scripts/gen-glossary.mjs` imports
// this module under plain Node with `--experimental-strip-types`, which resolves relative specifiers
// literally and cannot find an extensionless one. Vite and `tsc` both accept it
// (`allowImportingTsExtensions` is on), so the extension costs nothing and is the only form that
// works everywhere this file is loaded from.
//
// Adding this import without the extension silently broke `npm run gen:glossary` for two commits.
// Nothing caught it because the gate does not run the glossary generator - only the scenario check.
import { decorateTerm } from './term-debug.ts';

/**
 * Whether a term's lowercase forms may be produced by lower-casing it.
 *
 * `preserve` exists for words whose capitalisation is part of their identity - `iMIS` is the
 * standing example this repository already has a naming rule about. Defaulting to `title` rather
 * than requiring the policy on every term keeps the common case to three authored fields.
 */
export type CasingPolicy = 'title' | 'preserve';

/** What an author supplies. Omitting `plural` is a compile error, which is the point. */
export interface TermDefinition {
  /** Singular, exactly as the user sees it. */
  readonly value: string;
  /** Plural, authored because English pluralisation is not reliably mechanical. */
  readonly plural: string;
  /** What the concept IS, for the next author deciding whether this is the term they want. */
  readonly description: string;
  /** Defaults to `title`. Set `preserve` for words like iMIS that must never be lower-cased. */
  readonly casing?: CasingPolicy;
  /**
   * Overrides the derived singular possessive, for words where the derivation reads wrong -
   * English disagrees with itself after a trailing s ("Glass'" versus "Glass's").
   *
   * This is the ONLY authored form that can silently disagree with `value`, so it is re-checked
   * whenever `value` changes. See the vocabulary review question in
   * `documentation/engineering/qa-and-definition-of-done.md` section 5.
   */
  readonly possessive?: string;
}

/** What callers receive. Everything but `value` and `plural` is derived and cannot be authored. */
export interface Term {
  /** "Company" */
  readonly value: string;
  /** "Companies" */
  readonly plural: string;
  /** "company" - for mid-sentence prose. */
  readonly lower: string;
  /** "companies" */
  readonly lowerPlural: string;
  /** "Company's" */
  readonly possessive: string;
  /** "Companies'" */
  readonly pluralPossessive: string;
  /** "company's" */
  readonly lowerPossessive: string;
  /** "companies'" */
  readonly lowerPluralPossessive: string;
}

/**
 * Singular possessive: always `'s`, including after a trailing s.
 *
 * English takes `'s` on a singular noun even when it already ends in s - "the bus's wheels", "the
 * glass's edge". The bare apostrophe belongs to PLURALS, and conflating the two produced "iMIS'"
 * where "iMIS's" is wanted. Where usage genuinely disagrees, `TermDefinition.possessive` overrides.
 */
function singularPossessiveOf(word: string): string {
  return `${word}'s`;
}

/**
 * Plural possessive: a bare apostrophe when the plural ends in s, `'s` otherwise.
 *
 * "Members'" but "Children's". Mechanical in both directions, which is why no term authors it.
 */
function pluralPossessiveOf(plural: string): string {
  return /s$/i.test(plural) ? `${plural}'` : `${plural}'s`;
}

/** Applies the casing policy. `preserve` returns the word untouched so `iMIS` survives prose. */
function lowerBy(word: string, casing: CasingPolicy): string {
  return casing === 'preserve' ? word : word.toLowerCase();
}

/**
 * Expands one authored definition into the full set of forms.
 *
 * The singular possessive honours an authored override; the plural possessive never does, because
 * a plural already ending in s has an unambiguous form and a second override would be a second
 * place to drift.
 */
function resolveTerm(definition: TermDefinition): Term {
  const casing = definition.casing ?? 'title';
  const possessive =
    definition.possessive ?? singularPossessiveOf(definition.value);
  const pluralPossessive = pluralPossessiveOf(definition.plural);

  // Decoration is applied HERE, at the one point every form of every term passes through, so the
  // dev affordance needs no change at any call site and cannot miss a variant. It also covers the
  // stored-text path for free: `resolveTermPath` reads these same resolved forms, so a
  // `{{term ...}}` tag in a server-composed string decorates exactly like an interpolated one.
  // Off in production and off by default in dev - see `term-debug.ts`.
  return {
    value: decorateTerm(definition.value),
    plural: decorateTerm(definition.plural),
    lower: decorateTerm(lowerBy(definition.value, casing)),
    lowerPlural: decorateTerm(lowerBy(definition.plural, casing)),
    possessive: decorateTerm(possessive),
    pluralPossessive: decorateTerm(pluralPossessive),
    lowerPossessive: decorateTerm(lowerBy(possessive, casing)),
    lowerPluralPossessive: decorateTerm(lowerBy(pluralPossessive, casing)),
  };
}

/**
 * Builds the typed term object, preserving each key so `terms.loginGroup` is checked by the
 * compiler rather than looked up by string.
 *
 * The mapped return type is what makes a typo a build failure instead of a screen that says
 * "glossary" - the defect this whole system exists to remove.
 */
export function defineTerms<
  TDefinitions extends Record<string, TermDefinition>,
>(definitions: TDefinitions): { readonly [TKey in keyof TDefinitions]: Term } {
  const resolved: Record<string, Term> = {};
  for (const [key, definition] of Object.entries(definitions)) {
    resolved[key] = resolveTerm(definition);
  }
  return resolved as { readonly [TKey in keyof TDefinitions]: Term };
}

/**
 * The product's vocabulary, AS AUTHORED.
 *
 * Keys are camelCase identifiers naming the internal domain, never hyphenated: dotted access has to
 * parse both here and inside a `{{term ...}}` tag, so `terms.user-staff.value` is not available.
 *
 * Note that `member` and `company` were previously two separate glossary keys describing the SAME
 * entity - one the current word, one the word it is becoming. Keying on the domain collapses them
 * into this single `loginGroup` term, which is the clearest demonstration of why the rule is right.
 *
 * EXPORTED SEPARATELY FROM {@link terms} because `defineTerms` resolves definitions into `Term`s and
 * drops `description` and `casing` on the way - they are authoring metadata, not something a screen
 * renders. The acceptance criteria's glossary manifest needs exactly those two, so it reads this
 * object rather than the resolved one. Anything reading it must NOT re-derive `lower` and friends
 * for itself: the whole point of the resolution below is that there is one derivation.
 */
export const termDefinitions = {
  loginGroup: {
    // ONE term, not two (D-038). A LoginGroup IS a Company; it is ADDITIONALLY a Member when it
    // holds paying membership, which MembershipStatus models and the chips display. The second
    // term arrives with the first surface that genuinely needs the word Member, and not before -
    // naming a distinction with no screen to check it against is how a rename gets made twice.
    value: 'Company',
    plural: 'Companies',
    description:
      'An organisation AGWA deals with, whether or not it holds a membership. Entity: LoginGroup. It is additionally a Member when MembershipStatus says so; that standing is terms.membership, and is not this word (D-038).',
  },
  loginSite: {
    value: 'Site',
    plural: 'Sites',
    description:
      'A physical location belonging to a company. Entity: LoginSite.',
  },
  loginUser: {
    value: 'User',
    plural: 'Users',
    description: 'A person who signs in. Entity: LoginUser.',
  },
  // The person a site is contacted through. KEYED `siteOwner`, NOT `siteContact`: the internal
  // domain is unchanged - `LoginSite.OwnerLoginUserId`, `JobSetSiteOwner`, `SetSiteOwnerInput` and
  // `PUT sites/{id:guid}/owner` all still say Owner - and keying on the word the user currently
  // sees is what rule 1 above forbids. The customer renamed this word once; a key that moved with
  // it would have to move again on the next rename, and a key that has reached stored text is
  // permanent.
  //
  // VALUE IS THE BARE NOUN SO THE COMPOUND DERIVES. The label composes it with `loginSite` to read
  // "Site Contact"; the button uses it alone to read "Set Contact". A single `value: 'Site Contact'`
  // would freeze "Site" inside a value that no `loginSite` rename could reach, and could not yield
  // "Set Contact" without hand-authoring a second variant, which rule 2 forbids.
  siteOwner: {
    value: 'Contact',
    plural: 'Contacts',
    description:
      "The person who is a site's point of contact. Entity: LoginSite.OwnerLoginUserId.",
  },
  membership: {
    value: 'Membership',
    plural: 'Memberships',
    description:
      "A company's paying standing with AGWA, as distinct from the company itself. A company holding one is a Member, which is a standing and not a second name for the company (D-038).",
  },
  // The second term D-038 said would "arrive with the first surface that genuinely needs the word
  // Member, and not before". That condition is met several times over: the membership chips, the
  // filter rail, the legend explanations and the login banner all spell it.
  //
  // Keyed `member` because it names `MembershipStatus.Member`, the enum value - NOT the word
  // currently displayed. That is the same reasoning that makes `loginGroup` correct, and it is what
  // lets the displayed word change later without the key moving.
  //
  // DISTINCT FROM `membership`, which is the standing itself. "Membership" cannot mechanically
  // produce "Member", so one term could not serve both.
  member: {
    value: 'Member',
    plural: 'Members',
    description:
      'A company that holds paying AGWA membership. The standing itself is terms.membership; this is the word for a company that holds it. Enum: MembershipStatus.Member.',
  },
  staffUser: {
    value: 'Staff',
    plural: 'Staff',
    description:
      'A person working for an organisation: the system tenant (named by terms.agwa) or a company. The text names whose staff beside it (D-168). Plural is identical: "two Staff", never "Staffs".',
  },
  product: {
    value: 'Product',
    plural: 'Products',
    description:
      'A fenestration product in the Product Registry. Entity: Product.',
  },
  // The collection a Prime System Supplier site owns, as distinct from the MODULE called Product
  // Registry: a site holds a register, and Product Registry is what you open to work on it. That
  // is the same domain-versus-module split D-067 made for the security catalogue.
  //
  // KEYED `productRegister` THOUGH THE ENTITY IS `ProductRegistryOwner`, which is the first time a
  // key does not name its entity. "Owner" describes the company-level design being removed - the
  // row is about to BE the register rather than own one - and a key that has reached stored text
  // is permanent, so the key names where the entity is going rather than where it has been. The
  // entity rename that closes the gap is 1,609 lines across 143 files and is its own unit.
  productRegister: {
    value: 'Product Register',
    plural: 'Product Registers',
    description:
      'The collection of products a Prime System Supplier site owns and maintains. Entity: ProductRegistryOwner, which the key deliberately does not match - see the note above. NOT the Product Registry module, which is the surface a user opens to work on a register.',
  },
  // THE MODULE, as distinct from `productRegister` above: a Prime System Supplier site owns a
  // register, and Product Registry is the surface a user opens to work on it. D-068 drew that
  // distinction in prose and this is what makes it enforceable - two keys, so a rename of either
  // cannot silently move the other.
  //
  // Added 2026-08-29 during the operation type collapse, which needed the module's name as a
  // composition subject ("Product Registry Agent") and found the glossary did not own it. The word
  // is spelled literally 40 more times across `ui/src`; that sweep is tracked, not done here.
  productRegistry: {
    value: 'Product Registry',
    plural: 'Product Registries',
    description:
      'The module a user opens to work on a product register. Module code: Product.Registry. NOT terms.productRegister, which is the collection of products a site owns.',
  },
  // The module-wide list of test reports, as the menu, the route title and the breadcrumb root
  // all name it. Added 2026-09-06 when the breadcrumb root was spelled a third way ("Report
  // Registry") and the architect asked for one place an edit fixes everywhere.
  productTestReport: {
    value: 'Product Test Report',
    plural: 'Product Test Reports',
    description:
      'A test report (AS 2047, AS 5218, AS 1530) recorded against a product in the Product Registry; the plural names the module-wide list of them. Entity: ProductReport.',
  },
  // The sibling module term, added for the same reason and on the same day: it is the composition
  // subject in "Glass Accreditation Auditor". Spelled literally 29 more times across `ui/src`.
  glassAccreditation: {
    value: 'Glass Accreditation',
    plural: 'Glass Accreditations',
    description:
      'The module in which glass processing sites hold and maintain their accreditation certificate records. Module code: Glass.Accreditation.',
  },
  // Keyed `storedFile` for the entity, not `file`, for the same reason `loginGroup` is not
  // `company`: the key names the internal domain so the displayed word can change without the key
  // moving. Added 2026-08-23 because D-049 gave the word a precise meaning it did not have before,
  // and the file-storage acceptance documents lean on that meaning throughout.
  //
  // THERE IS DELIBERATELY NO `storedBlob` TERM. D-050 makes the blob invisible plumbing that a user
  // must never be shown, and a glossary entry would invite it into a sentence. The absence is the
  // rule, not an omission.
  storedFile: {
    value: 'File',
    plural: 'Files',
    description:
      'A named file a person uploads, sees in a browser and downloads. Entity: StoredFile. It is the BINDING - the name, category and path somebody chose - and not the bytes: two files holding identical bytes under different names are still two files (D-049). The bytes are a StoredBlob, which is not a term because it is invisible plumbing (D-050). Artefacts that HOLD files, such as a THERM file, get their own terms as they land.',
  },
  workflowState: {
    // Keyed on the concept - the workflow state a person drives (db/code word: State) -
    // not on the displayed word. The customer's word is "Status" (round-3 deck, "Change
    // word State to Status"); the architect kept State server-side and routed the UI
    // word through this term (2026-09-03), so if the customer flips the word again it
    // is one edit here, not a sweep of column titles and filter labels.
    value: 'Status',
    plural: 'Statuses',
    description:
      'The workflow state of a product or test report - the lifecycle a person drives (product: Submitted/Active/Retired/Deleted; report: Draft/Submitted/Under Review/Approved/Rejected/Superseded). Server field: State. Displays as the customer\u2019s word, currently "Status". NOT the compliance signal - that is terms.compliance.',
  },
  compliance: {
    // The evidence-derived signal (db word: Status, values PendingTest/Compliant).
    // Displays as "Compliance" because once the workflow word is "Status" (above),
    // two different columns cannot both carry it (D-088).
    value: 'Compliance',
    plural: 'Compliance',
    description:
      'The evidence-derived compliance signal on a product (PendingTest/Compliant) - written only by report approval, never set by hand. Server field: Status. Displays as "Compliance" to leave the word "Status" free for the workflow state.',
  },
  auditTrail: {
    // Keyed on the concept the *Audit tables record, not on the current heading. The word appears
    // as a literal in four places today (the shared component default plus three hosts), which is
    // exactly the drift this registry removes: one edit here retitles every trail at once.
    value: 'Audit Trail',
    plural: 'Audit Trails',
    description:
      'DATA AUDITING: the recorded history of what changed on a record, who changed it, from what, and when. Entities: the per-module audit tables such as AS2047ReportAudit and ProductAuditLog. Names the surface, never a single line in it - that is an entry. NOT the Audit Module (#260), which is AGWA auditing its members and shares only the word.',
  },
  auditorNote: {
    // The domain flag is IsAuditorNote, so the key names that rather than the "Auditor Notes"
    // heading three pages currently hard-code.
    value: 'Auditor Note',
    plural: 'Auditor Notes',
    description:
      "A note a reviewing auditor writes onto a record's audit trail, visible only to auditors and AGWA staff. Flag: IsAuditorNote on the audit row. Distinct from the field changes beside it, which the system records rather than a person writing them.",
  },
  userGroupDomain: {
    // Keyed on the entity, NOT on "operationType": keying on the display phrase would have made the
    // Organization to Operation change a key rename, which is exactly what D-026 exists to prevent.
    // That claim has now been tested rather than asserted - flipping the two values below performed
    // the whole rename, including audit rows already written, because the server stores tags and
    // never words (D-027).
    value: 'Operation Type',
    plural: 'Operation Types',
    description:
      "The classification of what kind of business a company or site operates. Entity: UserGroupDomain, which is what the product calls a domain. Renamed from 'Organization Type' per D-030 and D-038. The VALUES in that list are themselves terms, one per operation type, keyed on the domain rather than on AGWA's abbreviation - see terms.primeSystemSupplier and its siblings below.",
  },
  // ──────────────────────────────────────────────────────────────────────────
  // OPERATION TYPES - the values of terms.userGroupDomain
  // ──────────────────────────────────────────────────────────────────────────
  // These were deliberately EXEMPT from the glossary until 2026-08-29, on the reasoning that they
  // are AGWA's own names rather than the product's vocabulary. The exemption was wrong, and AGWA
  // proved it by renaming one: following a single word from "Glass Processor" to "Prime Glass
  // Manufacturer" meant editing the seeded displayName, the database column it seeds, a label map
  // in the UI, 22 lines across four acceptance documents and 19 journey files. Being somebody
  // else's word is a reason to own it in ONE place, not a reason to spell it in six.
  //
  // KEYED ON THE DOMAIN, NEVER ON THE ABBREVIATION. `ShortCode` is data precisely because AGWA
  // told us the abbreviations are not confirmed (D-067), so a `psm` key would make the most
  // volatile thing in the catalogue permanent. `primeGlassManufacturer` therefore does NOT read
  // `glassProcessor`: minting a permanent key on the word being retired would bake in the very
  // mistake this block exists to fix.
  //
  // Descriptions are AGWA's own words from security-catalog.yml, so the meaning has one source.
  primeSystemSupplier: {
    value: 'Prime System Supplier',
    plural: 'Prime System Suppliers',
    description:
      'Owns or controls system product data and supplies the product system or intellectual property. Domain: Prime.System.Supplier. The operation type that holds a product register.',
  },
  primeSystemManufacturer: {
    value: 'Prime System Manufacturer',
    plural: 'Prime System Manufacturers',
    description:
      'Manufactures finished products at a site using company-owned prime system IP. Primarily a site manufacturing and audit behaviour. Domain: Prime.System.Manufacturer. Carries no features yet and exists for the Audit Module (D-058).',
  },
  // The company's own product IP, and what the Product Registry actually hangs off: D-127 made this
  // the module owner and left Prime System Supplier and Prime System Manufacturer as the site
  // classifications that consume it. It is worn, never offered - the site that carries it is created
  // by the system - so the word exists here for the company row, the staff chips and the criteria,
  // not for an assignment control.
  //
  // KEYED ON THE DOMAIN, NEVER ON PIP, for the reason the block comment above gives: the short code
  // is the most volatile thing in the catalogue (D-067), so a `pip` key would make it permanent.
  //
  // `casing: 'preserve'` because the capitalisation IS the word. Without it `lower` yields "prime
  // system ip", and "ip" is not what AGWA means by IP. The derived possessive "Prime System IP's"
  // reads correctly, so no override is authored.
  primeSystemIntellectualProperty: {
    value: 'Prime System IP',
    plural: 'Prime System IPs',
    casing: 'preserve',
    description:
      'Prime System Intellectual Property: the product system IP a company owns, held on the hidden site its product register sits on. Short code PIP. The module owner of the Product Registry; Prime System Supplier and Prime System Manufacturer are the site classifications that consume it. Domain: Prime.System.IntellectualProperty.',
  },
  primeGlassManufacturer: {
    value: 'Prime Glass Manufacturer',
    plural: 'Prime Glass Manufacturers',
    description:
      'Produces cut-to-size or processed glass products at a site. Domain: Prime.Glass.Manufacturer, which AGWA confirmed on 2026-08-29 is their PGM - the word changed, the domain did not.',
  },
  primeGlassSupplier: {
    value: 'Prime Glass Supplier',
    plural: 'Prime Glass Suppliers',
    description:
      'Supplies foundational glass material and associated source performance data. Domain: Prime.Glass.Supplier.',
  },
  fabricator: {
    value: 'Fabricator',
    plural: 'Fabricators',
    description:
      'Manufactures finished products using systems supplied or authorised by a prime system supplier or manufacturer; does not own the prime product IP. Domain: Fabricator.',
  },
  reseller: {
    value: 'Reseller',
    plural: 'Resellers',
    description:
      'Purchases and resells finished products; does not own the underlying product IP. Domain: Reseller.',
  },
  installer: {
    value: 'Installer',
    plural: 'Installers',
    description:
      'Installs finished products on site; service behaviour only. Domain: Installer.',
  },
  glassWholesaler: {
    value: 'Glass Wholesaler/Merchant',
    plural: 'Glass Wholesalers/Merchants',
    description:
      'Wholesales or resells bulk or processed glass. Domain: Glass.Wholesaler.',
  },
  glazier: {
    value: 'Glazier',
    plural: 'Glaziers',
    description:
      'Installs glass and glazing systems; service behaviour only. Domain: Glazier.',
  },
  // AGWA itself, which the product names on screen: the system tenant's own workspace is labelled
  // with it, and the system tenant's `staffUser` is named beside it (D-168). `preserve` because the capitalisation IS
  // the word - `preserved-casing.ts` derives its protection list from this flag, so a column header
  // carrying `text-transform: uppercase` cannot quietly rewrite it either.
  agwa: {
    value: 'AGWA',
    plural: 'AGWA',
    casing: 'preserve',
    description:
      'The Australian Glass and Window Association - the tenant this product is operated by. Appears as the label of the AGWA.SystemTenant domain. Plural is identical because there is only one.',
  },
  // ──────────────────────────────────────────────────────────────────────────
  // DELEGATION RELATIONSHIPS - the second half of an access grant's name
  // ──────────────────────────────────────────────────────────────────────────
  // An access grant is named `<subject> <relationship>`: "Product Registry Agent", "Glass
  // Accreditation Auditor". The SUBJECT is a term already; these two are the relationship, and they
  // are terms for the same reason the subject is - the words were chosen deliberately to say which
  // relationship the holder has, and a chosen word spelled at a call site is a word that drifts.
  //
  // The distinction they carry, recorded because it is the reason the old "... Data Entry Team" and
  // "... Audit Team" names were replaced: an Agent acts ON BEHALF OF another member; an Auditor has
  // AUTHORITY OVER their records. Those are different relationships and the previous names carried
  // neither, which contributed to these grants being given to members who should have held nothing.
  agent: {
    value: 'Agent',
    plural: 'Agents',
    description:
      "A member that works on ANOTHER member's records on their behalf, without authority over them. The second half of a delegation access grant's name, e.g. terms.productRegistry + terms.agent.",
  },
  auditor: {
    value: 'Auditor',
    plural: 'Auditors',
    description:
      "A member with authority to review and verify ANOTHER member's records. The second half of a delegation access grant's name. Distinct from terms.auditorNote, which is the note such a reviewer writes.",
  },
  accreditingOrganization: {
    value: 'Accrediting Organization',
    plural: 'Accrediting Organizations',
    description:
      "The EXTERNAL body that issues an accreditation. Entity: AccreditingOrganization. Deliberately does NOT follow the Organization to Operation rename: that rename is specific to our own domain membership, and this names somebody else's organisation. Being a term means the word is owned in one place, not that it changes.",
  },
  imis: {
    value: 'iMIS',
    plural: 'iMIS',
    casing: 'preserve',
    description:
      'The external membership system. Never lower-cased, and never spelled IMIS or IMUS.',
  },
} as const satisfies Record<string, TermDefinition>;

/**
 * The vocabulary every surface reads, with the derived forms resolved.
 *
 * `satisfies` above rather than a type annotation on the definitions, so each term keeps its literal
 * key and this object stays checked by the compiler rather than looked up by string.
 */
export const terms = defineTerms(termDefinitions);

/** The term keys, for the substitution path's runtime lookup and for tests. */
export type TermKey = keyof typeof terms;

/** The derived forms a tag may address, e.g. the `plural` in `{{term loginGroup.plural}}`. */
export type TermVariant = keyof Term;
